import { useState } from "react";
import { ArrowRight, CheckCircle2, Crown, Zap, Shield, Star, Users, Clock, Globe, Building2, Mail, Phone, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Premium Quote Benefits Page
 * - For non-logged users or non-Professional users
 * - Shows premium benefits and upgrade options
 * - Styled to match SupplierLanding design with purple theme
 */

export default function PremiumQuoteBenefits() {
  const [activeTab, setActiveTab] = useState('exhibitor');

  const premiumBenefits = [
    {
      icon: Crown,
      title: "Exclusive Premium Contractors",
      description: "Access to top-tier contractors with proven track records and premium certifications",
      details: "Connect with elite contractors who have completed 50+ successful projects"
    },
    {
      icon: Zap,
      title: "Priority Response (2-4 Hours)",
      description: "Get quotes from premium contractors within 2-4 hours instead of 24-48 hours",
      details: "Your projects get immediate attention from our best contractors"
    },
    {
      icon: Shield,
      title: "Advanced Project Protection",
      description: "Enhanced escrow services with milestone guarantees and quality insurance",
      details: "100% money-back guarantee if deliverables don't meet agreed specifications"
    },
    {
      icon: Star,
      title: "Dedicated Account Manager",
      description: "Personal project consultant to guide you through the entire process",
      details: "Direct WhatsApp/phone line to your dedicated success manager"
    },
    {
      icon: Users,
      title: "Multi-Contractor Coordination",
      description: "We coordinate multiple contractors for complex projects seamlessly",
      details: "Perfect for large exhibitions requiring AV, construction, and catering coordination"
    },
    {
      icon: Clock,
      title: "Rush Project Support",
      description: "Last-minute project handling with emergency contractor network",
      details: "Projects can be delivered in as little as 7 days with premium network"
    }
  ];

  const comparisonFeatures = [
    { feature: "Response Time", standard: "24-48 hours", premium: "2-4 hours" },
    { feature: "Contractor Quality", standard: "Verified contractors", premium: "Premium + Elite only" },
    { feature: "Project Insurance", standard: "Basic escrow", premium: "Full insurance + guarantees" },
    { feature: "Account Support", standard: "Email support", premium: "Dedicated manager + phone" },
    { feature: "Rush Projects", standard: "14+ days minimum", premium: "7 days possible" },
    { feature: "Multi-Contractor", standard: "Self-coordinate", premium: "Full coordination included" }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HEADER */}
      <header className="bg-white px-6 py-4 relative z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
              alt="EXPO STAND Logo" 
              className="w-12 h-10" 
            />
            <div className="text-slate-700">
              <div className="text-xl font-semibold leading-tight">EXPO</div>
              <div className="text-xl font-semibold leading-tight">STAND</div>
            </div>
          </a>
          
          <div className="flex items-center space-x-6 text-slate-700 text-sm">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>hello@expostand.com</span>
            </div>
            
            {/* Sign In Dropdown */}
            <div className="relative group">
              <span className="hover:text-purple-600 cursor-pointer flex items-center">
                Sign In <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Exhibitor
                </a>
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Contractor
                </a>
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Supplier
                </a>
              </div>
            </div>
            
            {/* Sign Up Dropdown */}
            <div className="relative group">
              <span className="hover:text-purple-600 cursor-pointer flex items-center">
                Sign Up <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/exhibitor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Exhibitor
                </a>
                <a href="/contractor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Contractor
                </a>
                <a href="/supplier/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Supplier
                </a>
              </div>
            </div>
            
            <a href="/get-quote" className="hover:text-purple-600 cursor-pointer">Get Quote</a>
            <span className="hover:text-purple-600 cursor-pointer">Contact</span>
            <a 
              href="/admin-login"
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm rounded"
            >
              Admin Giriş
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden flex items-center" style={{ height: 'calc(100vh - 80px)' }}>
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2400&auto=format&fit=crop"
            alt="Expo hall background"
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/70 to-white" />
        </div>

        <div className="mx-auto max-w-7xl px-6 w-full pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm mb-2">
                <Sparkles className="h-4 w-4" />
                <span>Exclusive premium contractor network worldwide</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl leading-tight -mt-2">
                Welcome to the<br />
                <span className="text-purple-600">Premium Quote</span><br />
                Service
              </h1>
              <p className="mt-4 text-lg leading-7 text-slate-600">
                Exclusive access to elite contractors, priority handling, and white-glove project management. Connect with premium partners and experience excellence—available only for Professional plan members.
              </p>

              <div className="mt-6 space-y-2">
                <p className="text-sm text-slate-700">
                  <strong>👑 Elite Contractors</strong> - Access to top-tier contractors with proven track records
                </p>
                <p className="text-sm text-slate-700">
                  <strong>⚡ Priority Response</strong> - Get quotes within 2-4 hours instead of 24-48 hours
                </p>
                <p className="text-sm text-slate-700">
                  <strong>🛡️ Advanced Protection</strong> - Enhanced escrow with milestone guarantees
                </p>
                <p className="text-sm text-slate-700">
                  <strong>👨‍💼 Dedicated Manager</strong> - Personal project consultant and WhatsApp support
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="/exhibitor/signup"
                  className="inline-flex items-center gap-2 rounded-2xl bg-purple-600 px-5 py-3 text-white shadow hover:bg-purple-700 transition-colors"
                >
                  <Crown className="h-4 w-4" />
                  Upgrade to Professional
                </a>
                <a
                  href="/admin-login"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-slate-900 shadow-sm hover:bg-slate-50 transition-colors"
                >
                  Sign In to Account <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <ul className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600"/> Elite contractor access</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600"/> 2-4 hour response</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600"/> Full protection</li>
              </ul>
            </motion.div>

            {/* Right side image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=600&auto=format&fit=crop"
                  alt="Premium exhibition stand and professional service"
                  className="w-full h-[300px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-800">
                      Premium contractors delivering excellence worldwide
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating stats cards */}
              <div className="absolute -top-2 -right-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-600">2-4h</div>
                  <div className="text-xs text-slate-500">Response</div>
                </div>
              </div>
              
              <div className="absolute top-1/2 -left-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">Elite</div>
                  <div className="text-xs text-slate-500">Network</div>
                </div>
              </div>
              
              <div className="absolute -bottom-2 left-1/4 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">100%</div>
                  <div className="text-xs text-slate-500">Protected</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-purple-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Experience Premium Excellence—Here's How</h2>
            <p className="mt-2 text-slate-600 mb-4">Three enhanced steps from premium brief to elite contractor delivery.</p>
            <p className="text-slate-700">
              Skip the standard process and access our elite contractor network with dedicated support and guaranteed results. 
              <span className="font-medium text-purple-600"> Exclusive to Professional plan members | 95% client satisfaction rate.</span>
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Submit Premium Brief",
                desc: "Tell us your premium project requirements and we'll connect you with elite contractors who specialize in your industry and project type.",
                Icon: Crown,
                badge: "Step 1",
              },
              {
                title: "Elite Contractor Match",
                desc: "Receive priority quotes from our top-tier contractors within 2-4 hours. Each contractor is pre-screened for luxury projects and premium quality.",
                Icon: Star,
                badge: "Step 2",
              },
              {
                title: "White-Glove Delivery",
                desc: "Your dedicated account manager coordinates everything while you focus on your business. Full insurance and milestone protection included.",
                Icon: Shield,
                badge: "Step 3",
              },
            ].map(({ title, desc, Icon, badge }) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-purple-50" />
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">{badge}</div>
                <Icon className="mb-4 h-8 w-8" />
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="mt-2 text-slate-600">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PREMIUM BENEFITS */}
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Premium Service Benefits</h2>
            <p className="mt-2 text-slate-600">Experience the difference with our elite contractor network and dedicated support.</p>
          </div>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
          {premiumBenefits.map((benefit) => (
            <div key={benefit.title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="p-6">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-4 w-fit mb-4">
                  <benefit.icon className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm mb-3">{benefit.description}</p>
                <p className="text-xs text-slate-500 italic">{benefit.details}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Standard vs Premium Comparison
            </h2>
            <p className="text-xl text-slate-600">
              See what you get with Professional plan upgrade
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
            <div className="bg-gradient-to-r from-slate-50 to-purple-50 p-6 border-b border-slate-200">
              <div className="grid grid-cols-3 gap-6 items-center">
                <div className="font-semibold text-slate-900">Feature</div>
                <div className="text-center">
                  <div className="bg-slate-100 rounded-full py-2 px-4 font-semibold text-slate-700">
                    Standard Quote
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full py-2 px-4 font-semibold">
                    Premium Quote
                  </div>
                </div>
              </div>
            </div>

            {comparisonFeatures.map((item, index) => (
              <div 
                key={item.feature} 
                className={`grid grid-cols-3 gap-6 items-center p-6 ${index !== comparisonFeatures.length - 1 ? 'border-b border-slate-100' : ''}`}
              >
                <div className="font-medium text-slate-900">{item.feature}</div>
                <div className="text-center text-slate-600">{item.standard}</div>
                <div className="text-center">
                  <span className="bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent font-semibold">
                    {item.premium}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USER TYPE SELECTION */}
      <section className="py-20 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Ready to Get Premium Access?
            </h2>
            <p className="text-xl text-slate-600">
              Choose your path to premium quote service
            </p>
          </div>

          <div className="bg-white rounded-2xl p-2 shadow-lg mb-8">
            <div className="grid grid-cols-3 gap-2">
              {['exhibitor', 'contractor', 'supplier'].map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveTab(type)}
                  className={`py-3 px-6 rounded-xl font-semibold transition-all ${
                    activeTab === type
                      ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {type === 'exhibitor' ? 'Exhibitor' : type === 'contractor' ? 'Contractor' : 'Supplier'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                New to ExpoStand?
              </h3>
              <p className="text-slate-600 mb-6">
                Sign up for Professional plan and get immediate access to premium quote service
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-slate-700">7-day free trial</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-slate-700">Instant premium access</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-slate-700">Dedicated onboarding</span>
                </li>
              </ul>
              <a
                href={`#/${activeTab}/signup`}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-4 px-6 rounded-xl transition-all inline-flex items-center justify-center gap-2"
              >
                <Crown className="h-5 w-5" />
                Sign Up for Professional
              </a>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Existing User?
              </h3>
              <p className="text-slate-600 mb-6">
                Sign in to check your plan status or upgrade to Professional for premium access
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  <span className="text-slate-700">Check current plan</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  <span className="text-slate-700">Instant upgrade available</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  <span className="text-slate-700">Keep existing projects</span>
                </li>
              </ul>
              <a
                href="/admin-login"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all inline-flex items-center justify-center gap-2"
              >
                Sign In to Account <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Crown className="h-16 w-16 text-white mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready for Premium Treatment?
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Join thousands of successful exhibitors who choose premium service for their most important projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/exhibitor/signup"
                className="bg-white text-purple-600 hover:bg-purple-50 font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
              >
                Start 7-Day Free Trial
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-purple-600 font-semibold py-4 px-8 rounded-2xl transition-all inline-flex items-center justify-center gap-2"
              >
                <Phone className="h-5 w-5" />
                Speak with Expert
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xl font-bold">
              <Crown className="h-6 w-6 text-purple-600" />
              <span>ExpoStand Premium</span>
            </div>
            <p className="text-xs text-slate-500">© 2025 ExpoStand. Premium services available with Professional plan.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}